import { subClientsApi } from "#/api/subClientsApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import EntityAvatar from "#/components/ui/EntityAvatar";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import { INDUSTRY_LABELS, type SubClient } from "#/types/subClient";
import { Badge, Button, Table, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconEye, IconMail, IconPhone, IconPlus, IconTrash } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DeleteSubClientModal from "./components/DeleteSubClientModal";

const SubClientsPage = () => {
  const navigate = useNavigate();
  const { canWrite, canDelete } = usePermission();
  const { t } = useTranslation();

  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);
  const [toDelete, setToDelete] = useState<SubClient | undefined>(undefined);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [sortBy, setSortBy] = useState<string>("createdAt");

  const headers = [
    { key: "logo", value: t("subClients.columns.logo") },
    { key: "name", value: t("subClients.columns.name") },
    { key: "address", value: t("subClients.columns.address") },
    { key: "contact", value: t("subClients.columns.contact") },
    { key: "industry", value: t("subClients.columns.industry") },
    { key: "alertsCount", value: t("subClients.columns.alertsCount") },
    { key: "actions", value: t("common.actions") },
  ];
  // Indices of name / address / industry in `headers`.
  const sortableColumns = [1, 2, 4];

  const toTrimmed = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

  const toCount = (value: unknown): number =>
    typeof value === "number" && Number.isFinite(value) && value > 0 ? value : 0;

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["sub-clients", sortBy, sortDirection, page, limit],
    queryFn: async () => {
      const response = await subClientsApi.getAll({
        limit,
        page,
        ...(sortBy ? { sortBy } : {}),
        ...(sortDirection ? { order: sortDirection } : {}),
      });
      return {
        items: response.data.data,
        total: response.data.total,
        per_page: response.data.limit,
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    retry: false,
  });

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-hidden">
      <div className="z-10 flex items-center justify-between bg-white">
        <Title order={2}>{t("subClients.title")}</Title>
        <div className="flex w-fit items-center justify-center gap-4">
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={() => navigate("/sub-clients/new")}
              color="black"
              size="sm"
              disabled={!canWrite()}
            >
              {t("subClients.createNew")}
            </Button>
          </PermissionTooltip>
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={headers.map((h) => h.value)}
          loading={isPending}
          allowSorting={false}
          sortableColumns={sortableColumns}
          onSortChange={(e: TableSortProps) => {
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(headers[e.index].key);
          }}
          rows={items.map((sub) => {
            const email = toTrimmed(sub.contactPersonEmail);
            const phone = toTrimmed(sub.contactPersonPhone);
            const alertsCount = toCount(sub.alertsCount);

            return (
              <Table.Tr key={sub.id}>
                <Table.Td className="w-14">
                  <EntityAvatar src={sub.logo} name={sub.name} size={32} />
                </Table.Td>
                <Table.Td className="capitalize" style={{ minWidth: "200px" }}>
                  {sub.name ?? "--"}
                </Table.Td>
                <Table.Td style={{ minWidth: "260px" }}>{sub.address ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: "220px" }}>
                  {!email && !phone ? (
                    "--"
                  ) : (
                    <div className="flex flex-col gap-1">
                      {email && (
                        <a
                          href={`mailto:${email}`}
                          title={t("subClients.contact.sendEmail")}
                          className="inline-flex w-fit items-center gap-1.5 text-blue-600 hover:underline"
                        >
                          <IconMail size={16} className="shrink-0" />
                          <span className="break-all">{email}</span>
                        </a>
                      )}
                      {phone && (
                        <a
                          href={`tel:${phone.replace(/\s+/g, "")}`}
                          title={t("subClients.contact.call")}
                          className="inline-flex w-fit items-center gap-1.5 text-blue-600 hover:underline"
                        >
                          <IconPhone size={16} className="shrink-0" />
                          <span>{phone}</span>
                        </a>
                      )}
                    </div>
                  )}
                </Table.Td>
                <Table.Td style={{ minWidth: "180px" }}>
                  {sub.industry ? INDUSTRY_LABELS[sub.industry] : "--"}
                </Table.Td>
                <Table.Td style={{ minWidth: "140px" }}>
                  <Badge variant="light" color={alertsCount > 0 ? "red" : "gray"} radius="sm">
                    {alertsCount}
                  </Badge>
                </Table.Td>
                <Table.Td style={{ minWidth: "140px" }}>
                  <div className="flex w-full items-center justify-center gap-2">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => navigate(`/sub-clients/${sub.id}`)}
                    />
                    <PermissionTooltip disabled={!canDelete()}>
                      <ActionButton
                        icon={IconTrash}
                        variant="outline"
                        color="red"
                        disabled={!canDelete()}
                        onClick={() => {
                          setToDelete(sub);
                          openDeleteModal();
                        }}
                      />
                    </PermissionTooltip>
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <DeleteSubClientModal
        opened={deleteModalOpened}
        onClose={() => {
          closeDeleteModal();
          setToDelete(undefined);
        }}
        subClient={toDelete}
        onSuccess={() => {
          void refetch();
          setToDelete(undefined);
        }}
      />
    </div>
  );
};

export default SubClientsPage;
